
import { useState } from 'react'
import {
  Settings, User, Shield, Bell, Cpu, RefreshCw,
  Copy, CheckCircle2, AlertTriangle, Save, KeyRound, Globe,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { D1_WORKER_URL, PLATFORM_DOMAIN, WORKER_NAME } from '@/lib/constants'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3 border-b border-border/50 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description && <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  )
}

export default function SettingsPage() {
  const [saving, setSaving] = useState(false)
  const [email, setEmail] = useState('')
  const [cfAccountId, setCfAccountId] = useState('')
  const [cfAigToken, setCfAigToken] = useState('')
  const [rateLimitEnabled, setRateLimitEnabled] = useState(true)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true)
  const [saved, setSaved] = useState(false)

  async function handleSave() {
    setSaving(true)
    await new Promise(r => setTimeout(r, 800))
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-lava/10">
            <Settings className="w-4 h-4 text-lava" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Settings</h2>
            <p className="text-xs text-muted-foreground">Platform configuration and account management</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          size="sm"
          disabled={saving}
          className="gap-2 lava-gradient text-lava-foreground border-0 text-xs hover:opacity-90"
        >
          {saving ? (
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
          ) : saved ? (
            <CheckCircle2 className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saved ? 'Saved' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="account">
        <TabsList className="bg-muted border border-border h-8 p-0.5">
          <TabsTrigger value="account" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <User className="w-3 h-3" /> Account
          </TabsTrigger>
          <TabsTrigger value="platform" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <Cpu className="w-3 h-3" /> Platform
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <Shield className="w-3 h-3" /> Security
          </TabsTrigger>
        </TabsList>

        {/* Account */}
        <TabsContent value="account" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-lava" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Email address</Label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Display Name</Label>
                  <Input
                    placeholder="Your Name"
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Change Password</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input type="password" placeholder="Current password" className="h-8 text-xs" />
                  <Input type="password" placeholder="New password" className="h-8 text-xs" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* JWT Secret */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-lava" />
                JWT Signing Secret
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-warning/5 border border-warning/20">
                <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground">
                  Your JWT secret is unique per user and stored in D1. It signs your session tokens and is never exposed via the API. Rotating it will invalidate all active sessions.
                </p>
              </div>
              <div className="p-3 rounded-md bg-muted/50 border border-border">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">Current Secret (masked)</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs font-mono text-muted-foreground flex-1">••••••••••••••••••••••••••••••••</code>
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-muted-foreground hover:text-destructive px-2">
                    Rotate
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform */}
        <TabsContent value="platform" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-lava" />
                Infrastructure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                <SettingRow label="Platform Domain" description="Your LavaFlow platform URL">
                  <div className="flex items-center gap-2 bg-muted/50 border border-border rounded px-2 py-1">
                    <code className="text-[10px] font-mono text-foreground">{PLATFORM_DOMAIN}</code>
                    <CopyButton text={PLATFORM_DOMAIN} />
                  </div>
                </SettingRow>
                <SettingRow label="D1 REST Worker" description="Cloudflare D1 EdgeSQL endpoint">
                  <div className="flex items-center gap-2 bg-muted/50 border border-border rounded px-2 py-1">
                    <code className="text-[10px] font-mono text-foreground truncate max-w-[200px]">{D1_WORKER_URL}</code>
                    <CopyButton text={D1_WORKER_URL} />
                  </div>
                </SettingRow>
                <SettingRow label="Worker Name" description="Cloudflare Worker binding name">
                  <div className="flex items-center gap-2 bg-muted/50 border border-border rounded px-2 py-1">
                    <code className="text-[10px] font-mono text-foreground">{WORKER_NAME}</code>
                    <CopyButton text={WORKER_NAME} />
                  </div>
                </SettingRow>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Cpu className="w-4 h-4 text-lava" />
                Cloudflare AI Gateway
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">CF Account ID</Label>
                  <Input
                    value={cfAccountId}
                    onChange={e => setCfAccountId(e.target.value)}
                    placeholder="your-cf-account-id"
                    className="h-8 text-xs font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">CF AIG Token</Label>
                  <Input
                    value={cfAigToken}
                    onChange={e => setCfAigToken(e.target.value)}
                    type="password"
                    placeholder="••••••••••••••••"
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Used for <code className="font-mono text-lava">cf-aig-authorization</code> header on gateway requests.
                Get these from your Cloudflare dashboard → AI Gateway.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-lava" />
                Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                <SettingRow
                  label="Rate Limiting"
                  description="Enforce per-key request limits via Cloudflare Rate Limiter"
                >
                  <Switch
                    checked={rateLimitEnabled}
                    onCheckedChange={setRateLimitEnabled}
                    className="data-[state=checked]:bg-lava"
                  />
                </SettingRow>
                <SettingRow
                  label="Analytics Engine"
                  description="Log usage to Cloudflare Analytics Engine for real-time metrics"
                >
                  <Switch
                    checked={analyticsEnabled}
                    onCheckedChange={setAnalyticsEnabled}
                    className="data-[state=checked]:bg-lava"
                  />
                </SettingRow>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-lava" />
                Security Architecture
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { title: 'Web Crypto Key Signing', desc: 'API keys generated with crypto.getRandomValues() and signed via HMAC-SHA256', status: 'active' },
                { title: 'Zero Plaintext Storage', desc: 'Only SHA-256 hashes and fingerprints stored in D1. Raw keys never persisted.', status: 'active' },
                { title: 'Per-User JWT Secrets', desc: 'Each user has a unique JWT signing secret generated at registration time', status: 'active' },
                { title: 'HTTP-Only Session Cookies', desc: 'Sessions use HTTP-only, Secure, SameSite=Lax cookies — immune to XSS', status: 'active' },
                { title: 'Parameterized Queries', desc: 'All D1 queries use prepared statements — zero SQL injection surface', status: 'active' },
                { title: 'Timing-Safe Auth', desc: 'Login always runs full password verification to prevent timing attacks', status: 'active' },
                { title: 'Worker Secret Store', desc: 'LAVAFLOW_SECRET lives in Cloudflare Secrets Store, never in plain environment vars', status: 'active' },
              ].map(item => (
                <div key={item.title} className="flex items-start gap-3 p-3 rounded-md bg-success/5 border border-success/15">
                  <CheckCircle2 className="w-3.5 h-3.5 text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-foreground">{item.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-card border-border border-destructive/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-destructive flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-md border border-destructive/20 bg-destructive/5">
                <div>
                  <p className="text-xs font-semibold text-foreground">Revoke All API Keys</p>
                  <p className="text-[11px] text-muted-foreground">Immediately invalidate every active key across all services</p>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs border-destructive/40 text-destructive hover:bg-destructive/10">
                  Revoke All
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md border border-destructive/20 bg-destructive/5">
                <div>
                  <p className="text-xs font-semibold text-foreground">Delete Account</p>
                  <p className="text-[11px] text-muted-foreground">Permanently delete your account and all associated data</p>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-xs border-destructive/40 text-destructive hover:bg-destructive/10">
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
